/**
 * Translation runtime `i18n: I18n` (W1-12 ui-kit-i18n; contract src/i18n/types.ts, ARCHITECTURE.md §6.1, §13).
 *
 * Catalogues are collected with import.meta.glob and only their DEFAULT exports are read (no side-effect
 * registration, so no catalogue ever imports this module and there is no cycle):
 *   English: '/src/**\/*.messages.ts'  UI catalogues (defineMessages + catalogOf) and core/formats pure-data ones
 *   zh-CN:   '/src/i18n/zh-CN/*.ts'    one file per namespace (owned by the localisation package)
 * Each catalogue lands in the table of its declared `locale`. Lookup: current locale, then English. A key that
 * is missing everywhere renders as the key itself and is reported (console warning in dev, once per key); an
 * English fallback in another locale is recorded silently (see `missingKeys`). Formatting is ICU-lite (icu.ts)
 * with the plural rules and number formats of the locale the message text came from.
 *
 * `createI18n` builds an isolated instance from explicit catalogues (tests, tools); `i18n` is the app singleton.
 */

import type { Locale, MessageCatalog, MessageRef, Unsubscribe } from '../core/util/types';
import { formatMessage, IcuSyntaxError } from './icu';
import type { I18n, MessageKey } from './types';

/** Every UI locale, in the order the language pickers list them. */
export const LOCALES: readonly Locale[] = ['en', 'zh-CN'];

/** Native names for language pickers (not translated on purpose: users look for their own language). */
export const LOCALE_NAMES: Readonly<Record<Locale, string>> = { en: 'English', 'zh-CN': '简体中文' };

export type MissingKind = 'missing' | 'fallback' | 'syntax' | 'param' | 'duplicate';

export interface MissingReport {
  readonly key: string;
  readonly locale: Locale;
  readonly kind: MissingKind;
  readonly detail?: string;
}

export interface I18nRuntime extends I18n {
  /** Every problem seen since start (missing keys, English fallbacks, malformed messages, missing params). */
  missingKeys(): readonly MissingReport[];
  /** Fully qualified keys ("ns.id") present in a locale. */
  keys(locale?: Locale): readonly string[];
  /** Raw (unformatted) message text, or undefined. */
  message(key: string, locale?: Locale): string | undefined;
  /** Namespaces that have at least one catalogue in a locale. */
  namespaces(locale?: Locale): readonly string[];
}

export interface CreateI18nOptions {
  readonly catalogues: readonly MessageCatalog[];
  readonly locale?: Locale;
  /** Called for every new problem (default: console warning in dev for missing keys and syntax errors). */
  readonly report?: (r: MissingReport) => void;
  /** Mirror the locale into `<html lang>` (the app singleton does; isolated instances do not by default). */
  readonly syncDocumentLang?: boolean;
}

function isCatalogue(x: unknown): x is MessageCatalog {
  if (typeof x !== 'object' || x === null) return false;
  const c = x as Readonly<Record<string, unknown>>;
  const messages = c.messages;
  if (typeof c.ns !== 'string' || c.ns === '') return false;
  if (!(LOCALES as readonly unknown[]).includes(c.locale)) return false;
  if (typeof messages !== 'object' || messages === null) return false;
  return Object.values(messages).every((v) => typeof v === 'string');
}

/**
 * UI locale for a BCP 47 language tag (first-run default from `navigator.language`): any Chinese tag -> 'zh-CN'
 * (zh-TW is P1 and falls back to Simplified Chinese), everything else -> 'en'.
 */
export function localeFromLanguage(tag: string | undefined | null): Locale {
  return typeof tag === 'string' && /^zh\b/i.test(tag.trim()) ? 'zh-CN' : 'en';
}

function defaultReport(r: MissingReport): void {
  if (!import.meta.env.DEV) return;
  if (r.kind === 'fallback') return;
  console.warn(`[i18n] ${r.kind} ${r.locale} "${r.key}"${r.detail === undefined ? '' : `: ${r.detail}`}`);
}

export function createI18n(options: CreateI18nOptions): I18nRuntime {
  const tables = new Map<Locale, Map<string, string>>(LOCALES.map((l) => [l, new Map<string, string>()]));
  const nsByLocale = new Map<Locale, Set<string>>(LOCALES.map((l) => [l, new Set<string>()]));
  const report = options.report ?? defaultReport;
  const reported = new Set<string>();
  const problems: MissingReport[] = [];

  const note = (r: MissingReport): void => {
    const id = `${r.kind}|${r.locale}|${r.key}|${r.detail ?? ''}`;
    if (reported.has(id)) return;
    reported.add(id);
    problems.push(r);
    report(r);
  };

  for (const cat of options.catalogues) {
    const table = tables.get(cat.locale);
    const nsSet = nsByLocale.get(cat.locale);
    if (table === undefined || nsSet === undefined) continue;
    nsSet.add(cat.ns);
    for (const [id, text] of Object.entries(cat.messages)) {
      const key = `${cat.ns}.${id}`;
      if (table.has(key) && table.get(key) !== text) {
        note({ key, locale: cat.locale, kind: 'duplicate', detail: 'defined twice with different text' });
      }
      table.set(key, text);
    }
  }

  let locale: Locale = options.locale ?? 'en';
  const listeners = new Set<() => void>();

  const syncLang = (): void => {
    if (options.syncDocumentLang === true && typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  };
  syncLang();

  const lookup = (key: string): { readonly text: string; readonly from: Locale } | undefined => {
    const own = tables.get(locale)?.get(key);
    if (own !== undefined) return { text: own, from: locale };
    if (locale !== 'en') {
      const en = tables.get('en')?.get(key);
      if (en !== undefined) {
        note({ key, locale, kind: 'fallback' });
        return { text: en, from: 'en' };
      }
    }
    return undefined;
  };

  const t = (key: MessageKey | string, params?: Readonly<Record<string, string | number>>): string => {
    const hit = lookup(key);
    if (hit === undefined) {
      note({ key, locale, kind: 'missing' });
      return key;
    }
    try {
      return formatMessage(hit.text, params, hit.from, {
        onMissingParam: (name) => {
          note({ key, locale: hit.from, kind: 'param', detail: name });
        },
      });
    } catch (e) {
      if (e instanceof IcuSyntaxError) {
        note({ key, locale: hit.from, kind: 'syntax', detail: e.message });
        return hit.text;
      }
      throw e;
    }
  };

  return {
    get locale() {
      return locale;
    },
    t,
    tr: (ref: MessageRef) => t(ref.key, ref.params),
    ref: (key, params) => (params === undefined ? { key } : { key, params }),
    setLocale(l) {
      if (l === locale) return;
      locale = l;
      syncLang();
      for (const cb of [...listeners]) cb();
    },
    has: (key, l) => tables.get(l ?? locale)?.has(key) ?? false,
    subscribe(cb): Unsubscribe {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    missingKeys: () => [...problems],
    keys: (l) => [...(tables.get(l ?? locale)?.keys() ?? [])],
    message: (key, l) => tables.get(l ?? locale)?.get(key),
    namespaces: (l) => [...(nsByLocale.get(l ?? locale) ?? [])].sort(),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// App singleton
// ---------------------------------------------------------------------------------------------------------------

const englishModules = import.meta.glob<unknown>('/src/**/*.messages.ts', { eager: true, import: 'default' });
const zhModules = import.meta.glob<unknown>(['/src/i18n/zh-CN/*.ts', '!/src/i18n/zh-CN/*.test.ts'], {
  eager: true,
  import: 'default',
});

/** Default exports of every collected catalogue file, keyed by path (non-catalogue defaults are skipped). */
export const CATALOGUE_FILES: ReadonlyMap<string, MessageCatalog> = new Map(
  [...Object.entries(englishModules), ...Object.entries(zhModules)].flatMap(([path, value]) => {
    if (isCatalogue(value)) return [[path, value] as const];
    if (import.meta.env.DEV) console.warn(`[i18n] ${path} has no MessageCatalog default export; skipped`);
    return [];
  }),
);

export const i18n: I18nRuntime = createI18n({ catalogues: [...CATALOGUE_FILES.values()], syncDocumentLang: true });
